import React from 'react';
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { HiOutlineTicket, HiOutlineArrowsRightLeft, HiOutlineNoSymbol } from "react-icons/hi2";
import { FaSuitcaseRolling, FaBriefcase } from "react-icons/fa6";
import { Check } from "lucide-react";

export default function FareOptionsCard({
  option,
  rules,
  isSelected,
  onSelect,
  loading
}) {
  const baggageInfo = option.FareBreakDowns?.find(fb => fb.Passenger.Code === 'ADT')?.Baggages?.find(bg => bg.BagType === 'Luggage' || bg.BagType === 'Checked');
  const cabinBaggage = option.FareBreakDowns?.find(fb => fb.Passenger.Code === 'ADT')?.Baggages?.find(bg => bg.BagType === 'CarryOn' || bg.BagType === 'HandCarry' || bg.BagType === 'Hand Carry');

  const getPenaltyList = (ruleList, type) => {
    if (!ruleList || ruleList.length === 0) return ["N/A"];

    const relevantRules = ruleList.filter(r => {
      if (!r.Type) return true;
      const t = r.Type.toUpperCase();
      // For Sabre specific types
      if (t === 'REFUND' || t === 'CANCELLATION' || t === 'EXCHANGE' || t === 'CHANGE' || t === 'NOSHOW') {
        return (type === 'Refund' && (t === 'REFUND' || t === 'CANCELLATION')) ||
          (type === 'Exchange' && (t === 'EXCHANGE' || t === 'CHANGE')) ||
          (type === 'NoShow' && t === 'NOSHOW');
      }
      // For other types (like Travelport's RR2, CHG)
      return true;
    });

    // Group by Applicability first
    const apps = [...new Set(relevantRules.map(r => r.Applicability?.trim() || ""))];
    const resultParts = [];

    apps.forEach(app => {
      const rulesForApp = relevantRules.filter(r => (r.Applicability?.trim() || "") === app);

      // Separate by currency within the same applicability
      const currenciesInApp = [...new Set(rulesForApp.map(r => r.Currency || ""))];

      // If the fare currency exists in this set, we should probably only show that one
      // to keep the UI clean, otherwise show all currencies separately.  
      const displayCurrencies = currenciesInApp.includes(option.Currency)
        ? [option.Currency]
        : currenciesInApp;

      displayCurrencies.forEach(curr => {
        const rulesForCurr = rulesForApp.filter(r => (r.Currency || "") === curr);
        const penaltyAmounts = rulesForCurr
          .filter(r => r.Amount !== null && r.Amount !== undefined && r.Amount > 0)
          .map(r => r.Amount);

        let display = "";
        if (penaltyAmounts.length > 0) {
          const minAmount = Math.min(...penaltyAmounts);
          const maxAmount = Math.max(...penaltyAmounts);

          if (minAmount === maxAmount) {
            display = `${curr} ${minAmount.toLocaleString()}`;
          } else {
            display = `${curr} ${minAmount.toLocaleString()} - ${maxAmount.toLocaleString()}`;
          }
        } else {
          const allowedZero = rulesForCurr.find(r =>
            r.Amount === 0 && (
              (type === 'Refund' && r.Refundable !== false) ||
              (type === 'Exchange' && r.Changeable !== false) ||
              (type === 'NoShow' && (r.Refundable !== false || r.Changeable !== false))
            )
          );
          if (allowedZero) display = "Complimentary";
          else {
            const isDisallowed = rulesForCurr.some(r =>
              (type === 'Refund' && r.Refundable === false) ||
              (type === 'Exchange' && r.Changeable === false) ||
              (type === 'NoShow' && r.Refundable === false && r.Changeable === false)
            );
            if (isDisallowed) display = "Not Allowed";
          }
        }

        if (display) {
          const hasConditions = rulesForCurr.some(r => r.ConditionsApply === true);
          const shortApp = app
            .replace(/Before Departure/i, "Before Dep.")
            .replace(/After Departure/i, "After Dep.");
          resultParts.push(`${display}${shortApp ? ` (${shortApp})` : ""}${hasConditions ? "*" : ""}`);
        }
      });
    });

    return resultParts.length > 0 ? resultParts : ["Check Rules"];
  };

  return (
    <div className={cn(
      "flex flex-col border-2 rounded-xl p-4 min-w-[300px] w-[300px] transition-all",
      isSelected ? "border-primary bg-primary/5 shadow-md" : "border-muted bg-background hover:border-muted-foreground/50"
    )}>
      <div className="mb-4">
        <h3 className="text-xl font-bold">{option.Currency} {option.GTotalFare.toLocaleString()}</h3>
        <p className="text-xs uppercase font-semibold text-muted-foreground tracking-wider">{option.FareType}</p>
      </div>

      <Button
        variant={isSelected ? "default" : "outline"}
        className={cn("w-full mb-6 py-6 font-bold uppercase tracking-tight", isSelected && "cursor-default")}
        onClick={() => !isSelected && onSelect(option.BookingKey)}
      >
        {isSelected ? (
          <span className="flex items-center gap-2"><Check className="w-4 h-4" /> Selected</span>
        ) : "Select"}
      </Button>

      <div className="space-y-4 flex-1">
        <div className="flex gap-3">
          <HiOutlineTicket className="text-xl shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold">Cancellation Charge</p>
            <div className="text-xs text-muted-foreground">
              {loading ? "Loading..." : (
                getPenaltyList(rules?.CancellationRules, 'Refund').map((item, index) => (
                  <span key={index} className="block">
                    {index === 0 ? `Fee: ${item}` : item}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <HiOutlineArrowsRightLeft className="text-xl shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold">Change Charge</p>
            <div className="text-xs text-muted-foreground">
              {loading ? "Loading..." : (
                getPenaltyList(rules?.ChangeRules, 'Exchange').map((item, index) => (
                  <span key={index} className="block">
                    {index === 0 ? `Fee: ${item}` : item}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <HiOutlineNoSymbol className="text-xl shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold">No Show Charge</p>
            <div className="text-xs text-muted-foreground">
              {loading ? "Loading..." : (
                getPenaltyList(rules?.NoShowRules, 'NoShow').map((item, index) => (
                  <span key={index} className="block">
                    {index === 0 ? `Fee: ${item}` : item}
                  </span>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <FaBriefcase className="text-xl shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold">Cabin Baggage</p>
            <p className="text-xs text-muted-foreground">
              {cabinBaggage ? `${cabinBaggage.Quantity} ${cabinBaggage.Unit} per Adult` : "Check Details"}
            </p>
          </div>
        </div>

        <div className="flex gap-3">
          <FaSuitcaseRolling className="text-xl shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold">Check-in Baggage</p>
            <p className="text-xs text-muted-foreground">
              {baggageInfo ? `${baggageInfo.Quantity} ${baggageInfo.Unit} per Adult` : "Check Details"}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t flex justify-center">
        <Button variant="link" size="sm" className="text-muted-foreground hover:text-primary p-0 h-auto" onClick={() => onSelect(option.BookingKey, true)}>
          View Details &gt;
        </Button>
      </div>
    </div>
  );
}
